/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { INetworkResourceAvailability } from "../types";
import { NetworkResource, NetworkResourceFailureReason } from "../enums";
import type { DependencyConfig } from "./PropagationRule";
import { PropagationRule } from "./PropagationRule";
import { Log } from "../logging";

/**
 * Callback type used by nodes to report state changes back to the orchestrator.
 */
type NodeStateChangeCallback = (node: NetworkResourceNode) => void;

/**
 * Abstract base class for all network resource nodes.
 *
 * Each NetworkResource has a dedicated node subclass. The base class
 * provides availability state management, dependency wiring, and
 * the external-hint consumer contract.
 *
 * Subclasses may override setAvailability() to apply conditional logic
 * before adopting a propagated state from a parent node.
 */
abstract class NetworkResourceNode {
  protected static readonly TAG: string = "NetworkResourceNode";

  /** The network resource this node tracks. */
  protected readonly mResource: NetworkResource;

  /** Current availability state of this node. */
  protected mIsAvailable: boolean;

  /** Current failure reason, or null if available or not yet evaluated. */
  protected mFailureReason: NetworkResourceFailureReason | null;

  /**
   * Dependency map: target node → propagation rules.
   * When this node's state changes, dependent nodes are informed
   * using the configured propagation rule.
   */
  protected readonly mDependencies: Map<NetworkResourceNode, DependencyConfig>;

  /** Callback to report state changes to the orchestrator. */
  protected readonly mOnStateChanged: NodeStateChangeCallback;

  /**
   * Creates a new NetworkResourceNode.
   * All nodes start as unavailable with null failure reason (not yet evaluated).
   * @param resource - The network resource this node tracks
   * @param onStateChanged - Callback to report state changes to the orchestrator
   */
  constructor(
    resource: NetworkResource,
    onStateChanged: NodeStateChangeCallback
  ) {
    this.mResource = resource;
    this.mIsAvailable = false;
    this.mFailureReason = null;
    this.mDependencies = new Map();
    this.mOnStateChanged = onStateChanged;
  }

  /**
   * Lifecycle hook called once after all nodes are constructed and wired.
   * Override in subclasses that need initialization logic.
   */
  public init(): void {
    Log.d(
      NetworkResourceNode.TAG,
      "init() " + this.mResource + " node initialized"
    );
  }

  /**
   * Returns the network resource this node tracks.
   * @returns The network resource enum value
   */
  public getResource(): NetworkResource {
    return this.mResource;
  }

  /**
   * Returns the current availability state of this node.
   * @returns The current INetworkResourceAvailability
   */
  public getAvailability(): INetworkResourceAvailability {
    return {
      isAvailable: this.mIsAvailable,
      failureReason: this.mFailureReason
    };
  }

  /**
   * Returns a default unavailable availability state.
   * Used when no node exists for a queried resource.
   * @returns A INetworkResourceAvailability that is unavailable with no reason
   */
  public static createDefaultAvailability(): INetworkResourceAvailability {
    return { isAvailable: false, failureReason: null };
  }

  /**
   * Copies the availability state from the given source node onto this node.
   * Used during PROPAGATE rule application.
   * @param source - The node whose availability to copy
   */
  public copyAvailabilityFrom(source: NetworkResourceNode): void {
    this.setAvailability(source.mIsAvailable, source.mFailureReason);
  }

  /**
   * Sets the availability state of this node.
   * Subclasses may override to apply conditional logic before adopting state.
   * Only reports change to the orchestrator if state actually differs.
   * @param isAvailable - Whether the resource is available
   * @param failureReason - The failure reason, or null
   */
  public setAvailability(
    isAvailable: boolean,
    failureReason: NetworkResourceFailureReason | null
  ): void {
    const hasChanged =
      this.mIsAvailable !== isAvailable ||
      this.mFailureReason !== failureReason;

    if (hasChanged) {
      Log.i(
        NetworkResourceNode.TAG,
        "setAvailability() " +
          this.mResource +
          " changed: available=" +
          String(isAvailable) +
          ", reason=" +
          String(failureReason)
      );
      this.mIsAvailable = isAvailable;
      this.mFailureReason = failureReason;
      this.mOnStateChanged(this);
    }
  }

  /**
   * Start active probing to determine/re-determine the node's state.
   * Override in subclasses that perform active connectivity probing
   * (e.g., InternetNode).
   * @returns A promise that resolves when the re-evaluation is complete
   */
  public async invalidateState(): Promise<void> {
    Log.d(
      NetworkResourceNode.TAG,
      "invalidateState() " + this.mResource + " — no-op (base class)"
    );
  }

  /**
   * Cancel in-flight probing. Called when a parent goes unavailable
   * (no point probing Internet if LAN is down).
   * Override in subclasses that perform active connectivity probing.
   */
  public stopInvalidation(): void {
    Log.d(
      NetworkResourceNode.TAG,
      "stopInvalidation() " + this.mResource + " — no-op (base class)"
    );
  }

  /**
   * Register a dependency from this node to a target node with
   * propagation rules for unavailable and available transitions.
   * @param target - The dependent node to propagate to
   * @param onUnavailable - Rule when this node becomes unavailable
   * @param onAvailable - Rule when this node becomes available
   */
  public setDependency(
    target: NetworkResourceNode,
    onUnavailable: PropagationRule,
    onAvailable: PropagationRule
  ): void {
    this.mDependencies.set(target, { onUnavailable, onAvailable });
  }

  /**
   * Returns the dependency map for this node.
   * @returns Map of target nodes and their associated propagation rules
   */
  public getDependencies(): Map<NetworkResourceNode, DependencyConfig> {
    return this.mDependencies;
  }

  /**
   * Receives an external availability hint from the NetworkResourceEventDispatcher.
   * Default implementation is a no-op. Override in subclasses that accept hints.
   * @param resource - The resource whose availability may have changed
   * @param isAvailable - The suspected new availability (hint)
   * @param reason - The failure reason, if suspected unavailable
   * @returns A promise that resolves when the hint has been fully processed
   */
  public async onNetworkResourceAvailabilityHint(
    resource: NetworkResource,
    isAvailable: boolean,
    _reason: NetworkResourceFailureReason | null
  ): Promise<void> {
    Log.d(
      NetworkResourceNode.TAG,
      "onNetworkResourceAvailabilityHint() " +
        this.mResource +
        " received hint for " +
        resource +
        ": available=" +
        String(isAvailable)
    );
  }
}

export { NetworkResourceNode };
export type { NodeStateChangeCallback };
